<!--
부모컴포넌트에서 사용예시 :

<template>
  <div>
    <QuillEditor v-model="editorContent" :config="editorConfig" />
    <div>
      <h3>에디터 내용</h3>
      <div v-html="editorContent"></div>
    </div>
  </div>
</template>

<script>
import QuillEditor from "@/components/QuillEditor.vue";

export default {
  components: { QuillEditor },
  data() {
    return {
      editorContent: '<p>초기 값입니다.</p>', // 초기 데이터
      editorConfig: {
        htmlView: true
      },
    };
  },
};
</script>
//-->
<template>
  <div>
    <!-- Quill 에디터 -->
    <div 
      ref="editorContainer" 
      :class="{ 'quill-inline': resolvedConfig.inline }"
      :style="`min-height: ${resolvedConfig.inline ? 'auto' : config.minHeight};color:black`"
    ></div>

    <!-- HTML 및 Delta 보기/수정 모달 -->
    <AModal
      v-model="showModal"
      title="HTML / Delta"
      icon="code"
      size="lg"
      @close="closeModal"
    >
      <template #head>
        <div class="qe-tabs">
          <button
            type="button"
            class="qe-tab"
            :class="{ 'is-active': activeTab === 'html' }"
            @click="activeTab = 'html'"
          >
            <i class="material-icons-round">edit</i>
            HTML 수정
          </button>
          <button
            type="button"
            class="qe-tab"
            :class="{ 'is-active': activeTab === 'delta' }"
            @click="activeTab = 'delta'"
          >
            <i class="material-icons-round">data_object</i>
            Delta 보기
          </button>
        </div>
      </template>

      <div class="qe-editor">
        <textarea
          v-if="activeTab === 'html'"
          v-model="htmlContent"
          rows="18"
          class="qe-editor__textarea"
          spellcheck="false"
          aria-label="HTML 수정"
        ></textarea>
        <textarea
          v-if="activeTab === 'delta'"
          v-model="deltaString"
          rows="18"
          class="qe-editor__textarea"
          readonly
          spellcheck="false"
          aria-label="Delta 보기"
        ></textarea>
      </div>

      <template #foot>
        <ABtn kind="ghost" @click="closeModal">취소</ABtn>
        <ABtn
          v-if="activeTab === 'html'"
          kind="yellow"
          icon="save"
          @click="applyChanges"
        >반영하기</ABtn>
      </template>
    </AModal>

    <!-- 파일 관리 모달 -->
    <ExamFilesModal
      :show="showFileManager"
      @close="closeFileManager"
    />

    <!-- 에디터 컨트롤 버튼들 -->
    <div v-if="config.htmlView || config.fileManager" class="qe-controls">
      <ABtn
        v-if="config.fileManager"
        kind="ghost"
        icon="folder_special"
        type="button"
        @click="openFileManager"
      >파일 관리</ABtn>

      <ABtn
        v-if="config.htmlView"
        kind="ghost"
        icon="code"
        type="button"
        class="qe-controls__html"
        v-tooltip="$t('txt.16')"
        @click="openModal"
      >HTML/Delta 보기</ABtn>
    </div>
  </div>
</template>

<script>
import ExamFilesModal from "@/components/ExamFilesModal.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";

// 조건부 import를 위한 변수들
let BlotFormatter = null;

// quill-table-better 등록 (조건부) - 완전히 비활성화
async function registerTableModule() {
  // 테이블 모듈을 완전히 비활성화
  console.warn("Table module is disabled due to compatibility issues");
  return false;
}

// quill-blot-formatter 등록 (조건부)
let blotFormatterRegistered = false;
async function registerBlotFormatter() {
  if (!blotFormatterRegistered) {
    try {
      if (!BlotFormatter) {
        const module = await import("quill-blot-formatter");
        BlotFormatter = module.default;
      }
      
      // 모듈이 이미 등록되어 있는지 확인
      if (!Quill.imports['modules/blotFormatter']) {
        Quill.register("modules/blotFormatter", BlotFormatter);
      }
      blotFormatterRegistered = true;
    } catch (error) {
      console.warn("BlotFormatter failed to load or register:", error);
    }
  }
}

export default {
  name: "QuillEditor",
  components: { ExamFilesModal, AModal, ABtn },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    config: {
      type: Object,
      default: () => ({
        minHeight: "300px",
        htmlView: false,
        toolbarOptions: [],
        inline: false, // inline 모드 옵션 추가
        placeholder: "", // placeholder 옵션 추가
        tableSupport: false, // 테이블 지원 여부 (기본값: false로 변경)
        useBasicTable: false, // 기본 Quill 테이블 모듈 사용 여부
      }),
    },
  },
emits: ['update:modelValue'],
  data() {
    return {
      editor: null,
      htmlContent: "", // HTML 내용
      deltaContent: null, // Delta 객체
      deltaString: "", // Delta JSON 문자열
      showModal: false, // 모달 표시 상태
      activeTab: "html", // 활성화된 탭 (html or delta)
      showFileManager: false, // 파일 관리 모달 표시 여부
    };
  },
  computed: {
    resolvedConfig() {
      const isInline = this.config?.inline || false;
      
      // 부모로부터 props로 받은 config와 기본값 병합
      return {
        minHeight: this.config?.minHeight || "300px",
        htmlView: this.config?.htmlView || false,
        inline: isInline, // inline 모드 설정
        placeholder: this.config?.placeholder || "", // placeholder 설정
        tableSupport: this.config?.tableSupport !== false, // 테이블 지원 여부 (기본값: false)
        useBasicTable: this.config?.useBasicTable || false, // 기본 테이블 모듈 사용 여부
        toolbarOptions: this.config?.toolbarOptions?.length
          ? this.config.toolbarOptions
          : this.getDefaultToolbarOptions(isInline),
      };
    },
  },
  watch: {
    modelValue(newValue) {
      if (this.editor && this.editor.root.innerHTML !== newValue) {
        this.editor.root.innerHTML = newValue; // 부모의 데이터로 에디터 초기화
        this.htmlContent = newValue; // HTML 수정 영역도 업데이트
        this.deltaString = JSON.stringify(this.editor.getContents().ops, null, 2); // Delta 업데이트
      }
    },
  },
  mounted() {
    this.initializeQuill();
  },
  methods: {
    getDefaultToolbarOptions(isInline = false) {
      if (isInline) {
        // inline 모드용 간소화된 툴바 (테이블 제외)
        return [
          ['bold', 'italic', 'underline'],
          [{ color: [] }, { background: [] }],
          ['link']
        ];
      } else {
        // 기본 전체 툴바
        const toolbarOptions = [
          // 텍스트 포맷팅
          ['bold', 'italic', 'underline', 'strike'], // 굵게, 기울임, 밑줄, 취소선
          [{ script: 'sub' }, { script: 'super' }], // 첨자, 위첨자

          // 헤더 및 텍스트 크기
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // 헤더 크기
          [{ size: ['small', false, 'large', 'huge'] }], // 텍스트 크기
          [{ font: [] }], // 글꼴 선택

          // 색상
          [{ color: [] }, { background: [] }], // 텍스트 색상 및 배경색

          // 정렬 및 방향
          [{ align: [] }], // 텍스트 정렬 (좌, 우, 가운데, 양쪽 정렬)
          [{ direction: 'rtl' }], // 오른쪽에서 왼쪽 정렬 (RTL)

          // 리스트 및 들여쓰기
          [{ list: 'ordered' }, { list: 'bullet' }], // 순서가 있는/없는 리스트
          [{ indent: '-1' }, { indent: '+1' }], // 내어쓰기, 들여쓰기

          // 인용 및 코드 블록
          ['blockquote', 'code-block'], // 인용 블록 및 코드 블록

          // 삽입 기능
          ['link', 'image', 'video'], // 링크, 이미지, 비디오 삽입

          // 초기화
          ['clean'], // 포맷 초기화
        ];

        // 기본 테이블 모듈이 활성화된 경우에만 테이블 버튼 추가
        if (this.config?.tableSupport && this.config?.useBasicTable) {
          toolbarOptions.splice(-1, 0, ['table']); // clean 버튼 앞에 테이블 버튼 추가
        }

        return toolbarOptions;
      }
    },
    async initializeQuill(){
      if (!this.$refs.editorContainer) {
        console.error("Editor container not found");
        return;
      }

      // Vue DOM 렌더링 완료 후 초기화
      await this.$nextTick();
      
      const isInline = this.resolvedConfig.inline;
      
      if (isInline) {
        // 인라인 모드: 간단한 에디터만 초기화 (테이블 모듈 완전 제외)
        await this.initializeInlineEditor();
      } else {
        // 표준 모드: 모든 모듈 포함
        await this.initializeStandardEditor();
      }
    },

    async initializeInlineEditor() {
      const modules = {
        toolbar: this.resolvedConfig.toolbarOptions,
      };

      const options = {
        theme: 'bubble',
        modules: modules,
        placeholder: this.resolvedConfig.placeholder,
      };

      // 인라인 전용 Quill 에디터 초기화
      this.editor = new Quill(this.$refs.editorContainer, options);

      // 이벤트 리스너 등록
      this.setupEditorEvents();
    },

    async initializeStandardEditor() {
      // 표준 모드에서만 고급 모듈들 등록
      let tableModuleAvailable = false;
      
      // tableSupport가 활성화된 경우에만 테이블 모듈 로드 시도
      if (this.resolvedConfig.tableSupport) {
        if (this.resolvedConfig.useBasicTable) {
          // 기본 Quill 테이블 모듈 사용
          tableModuleAvailable = true;
        } else {
          // quill-table-better 사용 (현재 비활성화됨)
          try {
            await registerTableModule();
            tableModuleAvailable = false; // 항상 false로 설정
          } catch (error) {
            console.warn("Table module initialization failed:", error);
          }
        }
      }
      
      await registerBlotFormatter();
        
      const modules = {
        toolbar: {
          container: this.resolvedConfig.toolbarOptions,
          handlers: {
            image: this.imageHandler,
          },
        },
        blotFormatter: {},
      };

      // 기본 테이블 모듈 사용
      if (tableModuleAvailable && this.resolvedConfig.useBasicTable) {
        modules.table = true; // 기본 테이블 모듈 활성화
      } else {
        modules.table = false; // 테이블 모듈 비활성화
      }

      const options = {
        theme: 'snow',
        modules: modules,
        placeholder: this.resolvedConfig.placeholder,
      };

      // 표준 Quill 에디터 초기화
      this.editor = new Quill(this.$refs.editorContainer, options);

      // 이벤트 리스너 등록
      this.setupEditorEvents();
    },

    setupEditorEvents() {
      // Quill 에디터 내용 변경 이벤트 처리
      this.editor.on("text-change", () => {
        const html = this.editor.root.innerHTML;
        const delta = this.editor.getContents();
        this.htmlContent = html;
        this.deltaContent = delta;
        this.deltaString = JSON.stringify(delta.ops, null, 2);

        this.$emit("update:modelValue", html);
      });

      // 초기 값 설정
      this.editor.root.innerHTML = this.modelValue;
      this.deltaContent = this.editor.getContents();
      this.deltaString = JSON.stringify(this.deltaContent.ops, null, 2);
    },
    openModal() {
      // 현재 HTML 및 Delta 데이터 설정
      this.htmlContent = this.editor.root.innerHTML;
      this.deltaString = this.deltaContent
        ? JSON.stringify(this.deltaContent.ops, null, 2)
        : ""; // Delta가 null인지 확인
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    applyChanges() {
      if (this.activeTab === "html") {
        // HTML 수정 내용 반영
        this.editor.root.innerHTML = this.htmlContent;
      } else if (this.activeTab === "delta") {
        try {
          // Delta 수정 내용 반영
          const parsedDelta = JSON.parse(this.deltaString);
          this.editor.setContents({ ops: parsedDelta });
        } catch (error) {
          alert("Delta 데이터가 올바르지 않습니다!");
          console.error("Delta JSON 파싱 오류:", error);
        }
      }

      // Quill 에디터의 변경 사항을 부모로 전달
      const html = this.editor.root.innerHTML;
      this.$emit("update:modelValue", html);
      this.showModal = false;
    },
    // 개선된 이미지 핸들러 메서드 - 파일명 자동 URL 변환 지원
    async imageHandler() {
      const hasFileManager = this.config?.fileManager;
      const isExamFilesMode = this.config?.examFilesMode || false;
      
      let promptMessage;
      if (isExamFilesMode && hasFileManager) {
        promptMessage = '이미지 파일명을 입력하세요.\n(파일 관리에서 업로드한 파일명을 복사하여 붙여넣기)\n\n예: image123.jpg → 자동으로 전체 URL 변환됨';
      } else if (hasFileManager) {
        promptMessage = '이미지 URL을 입력하세요.\n(팁: 하단의 "파일 관리" 버튼으로 이미지를 업로드할 수 있습니다)';
      } else {
        promptMessage = '이미지 URL을 입력하세요:';
      }
        
      const input = prompt(promptMessage);
      if (input && input.trim()) {
        let imageUrl = input.trim();
        
        // URL 변환 로직: 문제은행 모드에서만 파일명을 API 엔드포인트로 변환
        if (isExamFilesMode && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('data:')) {
          // 문제은행 모드에서 파일명만 입력된 경우 전체 URL로 변환
          imageUrl = `${this.$CONF.ACHIEVE_API_URL}/files/image/stream/${imageUrl}?file_type=exam_files`;
        }
        
        try {
          // 에디터가 존재하고 활성화되어 있는지 확인
          if (!this.editor) {
            console.error('Editor is not initialized');
            return;
          }
          
          // 에디터에 포커스를 주어 선택 영역을 활성화
          this.editor.focus();
          
          // 포커스가 제대로 설정되도록 잠시 대기
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // 안전하게 커서 위치 가져오기
          let range = null;
          try {
            range = this.editor.getSelection(true); // true 옵션으로 강제 포커스
          } catch (selectionError) {
            console.warn('Selection error, using fallback:', selectionError);
            range = null;
          }
          
          // range가 여전히 null이면 한 번 더 시도
          if (!range) {
            try {
              await new Promise(resolve => setTimeout(resolve, 100));
              range = this.editor.getSelection(true);
            } catch (retryError) {
              console.warn('Retry selection failed:', retryError);
              range = null;
            }
          }
          
          if (range && range.index !== undefined && range.index !== null) {
            // 현재 커서 위치에 이미지 삽입
            this.editor.insertEmbed(range.index, 'image', imageUrl);
            
            // 커서를 이미지 다음으로 이동
            try {
              this.editor.setSelection(range.index + 1);
            } catch (setSelectionError) {
              console.warn('Set selection error:', setSelectionError);
            }
          } else {
            // 커서 위치가 없거나 오류가 있으면 문서 끝에 삽입
            const length = this.editor.getLength();
            const insertIndex = Math.max(0, length - 1);
            this.editor.insertEmbed(insertIndex, 'image', imageUrl);
            
            try {
              this.editor.setSelection(insertIndex + 1);
            } catch (setSelectionError) {
              console.warn('Set selection error at end:', setSelectionError);
            }
          }
          
          // 변경 사항 전파
          const html = this.editor.root.innerHTML;
          this.$emit("update:modelValue", html);
          
        } catch (error) {
          console.error('Image insertion error:', error);
          
          // 폴백: 직접 HTML 삽입 방식
          try {
            const currentContent = this.editor.root.innerHTML;
            const imgTag = `<img src="${imageUrl}" alt="이미지">`;
            this.editor.root.innerHTML = currentContent + imgTag;
            
            // 변경 사항 전파
            const html = this.editor.root.innerHTML;
            this.$emit("update:modelValue", html);
          } catch (fallbackError) {
            console.error('Fallback image insertion failed:', fallbackError);
            alert('이미지 삽입 중 오류가 발생했습니다.');
          }
        }
      }
    },
    
    // 파일 관리 모달 열기
    openFileManager() {
      this.showFileManager = true;
    },
    
    // 파일 관리 모달 닫기
    closeFileManager() {
      this.showFileManager = false;
    },
  },
};
</script>

<style scoped>
@import "~quill/dist/quill.snow.css";
@import "~quill/dist/quill.bubble.css";

/* quill-table-better CSS는 조건부로 로드됨 */

/* Inline 모드 스타일링 */
.quill-inline {
  border: none;
  border-bottom: 1px solid #ccc;
  border-radius: 0;
  background: #fff;
}

.quill-inline .ql-editor {
  min-height: auto;
  padding: 8px 12px;
  line-height: 1.4;
}

.quill-inline .ql-tooltip {
  z-index: 1001;
}

/* Quill 툴팁과 색상 팔레트가 다른 요소들 위에 표시되도록 z-index 설정 */
:deep(.ql-tooltip) {
  z-index: 9999 !important;
}

:deep(.ql-picker-options) {
  z-index: 9999 !important;
}

:deep(.ql-color-picker .ql-picker-options) {
  z-index: 9999 !important;
}

:deep(.ql-background .ql-picker-options) {
  z-index: 9999 !important;
}

/* Placeholder 스타일링 */
.ql-editor.ql-blank::before {
  color: #999 !important;
  font-style: normal !important;
  font-size: 16px !important;
  font-weight: normal !important;
  font-family: inherit !important;
}

/* 전역 placeholder 스타일 (scoped가 아닌 경우를 대비) */
:deep(.ql-editor.ql-blank::before) {
  color: #999 !important;
  font-style: normal !important;
  font-size: 16px !important;
  font-weight: normal !important;
  font-family: inherit !important;
}

/* Pineapple HTML / Delta 모달 — Textbook-Log §2.10 (다크 잉크 + 옐로 active + 터미널 톤) */
.qe-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #F4EFE0;
  border: 1px solid #E6DCC4;
  border-radius: 10px;
}

.qe-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  background: transparent;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
  font-family: 'Pretendard', -apple-system, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #6B6359;
  transition: background 0.12s, color 0.12s, box-shadow 0.12s;
}

.qe-tab i {
  font-size: 16px !important;
}

.qe-tab:hover:not(.is-active) {
  background: #FFFFFF;
  color: #1F1B14;
}

.qe-tab.is-active {
  background: #1F1B14;
  color: #FFC93C;
  font-weight: 800;
  box-shadow: 0 2px 6px rgba(31, 27, 20, 0.18);
}

.qe-tab.is-active i {
  color: #FFC93C;
}

.qe-editor {
  margin-top: 4px;
}

.qe-editor__textarea {
  width: 100%;
  min-height: 380px;
  padding: 14px 16px;
  background: #1F1B14;
  color: #FFC93C;
  border: 1px solid #2B241D;
  border-radius: 8px;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  letter-spacing: 0.01em;
  resize: vertical;
  outline: none;
  white-space: pre;
  overflow: auto;
  caret-color: #FFC93C;
}

.qe-editor__textarea::selection {
  background: rgba(255, 201, 60, 0.28);
  color: #FFFFFF;
}

.qe-editor__textarea:focus {
  border-color: #C98A0A;
  box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.2);
}

.qe-editor__textarea[readonly] {
  color: #F6EFDB;
  cursor: default;
}

/* Editor control buttons below the Quill editor */
.qe-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}

.qe-controls__html {
  margin-left: auto;
}
</style>
